// ===========================================
// DEMO 2 PAGE - RAKE ARC ANIMATION TUNING
// ===========================================
// Focused testbed for perfecting rake arc trajectories
// from each seat through/near dealer to final destination.
// ===========================================

import { useState, useCallback, useRef, useEffect } from 'react';
import cardBack from '../assets/cards/card_back.svg';

// ===========================================
// CANVAS CONFIGURATION (matches GameTable.tsx)
// ===========================================

const CANVAS_WIDTH = 1920;
const CANVAS_HEIGHT = 1080;

// ===========================================
// SEAT POLAR POSITIONS (from GameTable.tsx)
// ===========================================

const TABLE_CENTER = { x: 50, y: 20 }; // Percentage coordinates

const SEAT_POLAR_POSITIONS = [
  { visualPos: 1, angle: 170, radius: 41, rotation: 90 },   // Far left
  { visualPos: 2, angle: 144, radius: 47, rotation: 60 },   // Left
  { visualPos: 3, angle: 120, radius: 48, rotation: 30 },   // Inner left
  { visualPos: 4, angle: 90,  radius: 46, rotation: 0 },    // Center (player)
  { visualPos: 5, angle: 60,  radius: 48, rotation: -30 },  // Inner right
  { visualPos: 6, angle: 36,  radius: 47, rotation: -60 },  // Right
  { visualPos: 7, angle: 10,  radius: 41, rotation: -90 },  // Far right
];

function polarToCartesian(angle: number, radius: number): { x: number; y: number } {
  const radians = (angle * Math.PI) / 180;
  return {
    x: TABLE_CENTER.x + radius * Math.cos(radians),
    y: TABLE_CENTER.y + radius * Math.sin(radians),
  };
}

const VISUAL_POSITIONS = SEAT_POLAR_POSITIONS.map((seat) => {
  const { x, y } = polarToCartesian(seat.angle, seat.radius);
  return {
    visualPos: seat.visualPos,
    x,
    y,
    rotation: seat.rotation,
    angle: seat.angle,
    radius: seat.radius,
  };
});

// ===========================================
// RAKE DESTINATION (canvas coordinates)
// ===========================================
const RAKE_DESTINATION = { x: 960, y: -200 }; // Above dealer center
const DEALER_CENTER = { x: 50, y: 20 }; // Percentage - same as TABLE_CENTER

// ===========================================
// CARD LAYOUT WITHIN SEAT
// ===========================================
// Cards are offset from seat center, staggered horizontally
const CARD_OFFSETS = [
  { dx: -30, dy: 0 },  // Left card
  { dx: 0, dy: -5 },   // Center card (slightly up)
  { dx: 30, dy: 0 },   // Right card
];

// ===========================================
// ARC CONFIGURATION TYPE
// ===========================================
interface ArcConfig {
  // Control point offset - how much to pull the arc toward dealer
  controlPointPull: number; // 0 = straight line, 1 = through dealer center
  // Vertical offset of control point (percentage of canvas height)
  controlPointY: number; // Where the arc peaks vertically (0% = top, 50% = middle)
  // Animation duration in ms
  duration: number;
  // Stagger delay between cards in same seat
  staggerDelay: number;
}

const DEFAULT_ARC_CONFIG: ArcConfig = {
  controlPointPull: 1.1, // Pull past dealer center for smooth arc
  controlPointY: 35, // 35% from top - through dealer area
  duration: 450,
  staggerDelay: 150,
};

// ===========================================
// QUADRATIC BEZIER HELPERS
// ===========================================

// Calculate point on quadratic bezier curve at time t
function quadraticBezier(
  p0: { x: number; y: number },
  p1: { x: number; y: number }, // Control point
  p2: { x: number; y: number },
  t: number
): { x: number; y: number } {
  const oneMinusT = 1 - t;
  return {
    x: oneMinusT * oneMinusT * p0.x + 2 * oneMinusT * t * p1.x + t * t * p2.x,
    y: oneMinusT * oneMinusT * p0.y + 2 * oneMinusT * t * p1.y + t * t * p2.y,
  };
}

// Generate SVG path for quadratic bezier
function quadraticBezierPath(
  start: { x: number; y: number },
  control: { x: number; y: number },
  end: { x: number; y: number }
): string {
  return `M ${start.x} ${start.y} Q ${control.x} ${control.y} ${end.x} ${end.y}`;
}

// Calculate control point for arc from a specific position to rake destination
function calculateControlPoint(
  startX: number, // Canvas pixels
  startY: number,
  config: ArcConfig
): { x: number; y: number } {
  // Dealer center in canvas pixels
  const dealerX = (DEALER_CENTER.x / 100) * CANVAS_WIDTH;

  // Interpolate X between start and dealer based on pull amount
  const controlX = startX + (dealerX - startX) * config.controlPointPull;

  // Y is based on controlPointY (percentage of canvas)
  const controlY = (config.controlPointY / 100) * CANVAS_HEIGHT;

  return { x: controlX, y: controlY };
}

// ===========================================
// ANIMATED CARD COMPONENT
// ===========================================
function AnimatedCard({
  startX,
  startY,
  controlX,
  controlY,
  endX,
  endY,
  rotation,
  duration,
  delay,
  onComplete,
}: {
  startX: number;
  startY: number;
  controlX: number;
  controlY: number;
  endX: number;
  endY: number;
  rotation: number;
  duration: number;
  delay: number;
  onComplete: () => void;
}) {
  const cardRef = useRef<HTMLImageElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const delayTimer = setTimeout(() => {
      setVisible(true);
    }, delay);

    return () => clearTimeout(delayTimer);
  }, [delay]);

  useEffect(() => {
    if (!cardRef.current || !visible) return;

    // Generate keyframes along the bezier curve
    const keyframes: Keyframe[] = [];
    const steps = 20;

    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const pos = quadraticBezier(
        { x: startX, y: startY },
        { x: controlX, y: controlY },
        { x: endX, y: endY },
        t
      );

      // Interpolate rotation toward 0
      const currentRotation = rotation * (1 - t);

      keyframes.push({
        transform: `translate(${pos.x}px, ${pos.y}px) translate(-50%, -50%) rotate(${currentRotation}deg)`,
        opacity: t < 0.7 ? 1 : 1 - (t - 0.7) / 0.3, // Fade out in last 30%
      });
    }

    const animation = cardRef.current.animate(keyframes, {
      duration,
      easing: 'ease-in-out',
      fill: 'forwards',
    });

    animation.onfinish = onComplete;

    return () => animation.cancel();
  }, [startX, startY, controlX, controlY, endX, endY, rotation, duration, visible, onComplete]);

  if (!visible) return null;

  return (
    <img
      ref={cardRef}
      src={cardBack}
      alt="Card"
      className="absolute w-16 h-24 object-contain pointer-events-none z-50"
      style={{
        left: 0,
        top: 0,
        transform: `translate(${startX}px, ${startY}px) translate(-50%, -50%) rotate(${rotation}deg)`,
      }}
    />
  );
}

// ===========================================
// STATIC CARD IN SEAT
// ===========================================
function SeatCard({
  x,
  y,
  rotation,
  hidden,
}: {
  x: number;
  y: number;
  rotation: number;
  hidden: boolean;
}) {
  if (hidden) return null;

  return (
    <img
      src={cardBack}
      alt="Card"
      className="absolute w-16 h-24 object-contain pointer-events-none"
      style={{
        left: 0,
        top: 0,
        transform: `translate(${x}px, ${y}px) translate(-50%, -50%) rotate(${rotation}deg)`,
      }}
    />
  );
}

// ===========================================
// MAIN DEMO 2 PAGE
// ===========================================
export function Demo2Page() {
  const [arcConfig, setArcConfig] = useState<ArcConfig>(DEFAULT_ARC_CONFIG);
  const [selectedSeat, setSelectedSeat] = useState<number>(4);
  // Track which seats are currently animating (their cards are hidden)
  const [rakingSeats, setRakingSeats] = useState<Set<number>>(new Set());
  // Track individual animating cards: { seatNum, cardIndex }[]
  const [animatingCards, setAnimatingCards] = useState<{ seat: number; card: number; id: number }[]>([]);
  const [showPaths, setShowPaths] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const nextCardId = useRef(0);

  // Calculate scale on mount and resize
  useEffect(() => {
    const updateScale = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const newScale = Math.min(containerWidth / CANVAS_WIDTH, 1);
        setScale(newScale);
      }
    };

    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, []);

  // Get seat position in canvas pixels
  const getSeatCanvasPosition = useCallback((visualPos: number) => {
    const seat = VISUAL_POSITIONS.find((s) => s.visualPos === visualPos)!;
    return {
      x: (seat.x / 100) * CANVAS_WIDTH,
      y: (seat.y / 100) * CANVAS_HEIGHT,
      rotation: seat.rotation,
    };
  }, []);

  // Get card position within a seat (canvas pixels)
  const getCardPosition = useCallback((visualPos: number, cardIndex: number) => {
    const seatPos = getSeatCanvasPosition(visualPos);
    const offset = CARD_OFFSETS[cardIndex];

    // Rotate the offset based on seat rotation
    const rotRad = (seatPos.rotation * Math.PI) / 180;
    const rotatedDx = offset.dx * Math.cos(rotRad) - offset.dy * Math.sin(rotRad);
    const rotatedDy = offset.dx * Math.sin(rotRad) + offset.dy * Math.cos(rotRad);

    return {
      x: seatPos.x + rotatedDx,
      y: seatPos.y + rotatedDy,
      rotation: seatPos.rotation,
    };
  }, [getSeatCanvasPosition]);

  // Rake a single seat
  const rakeSeat = useCallback((seatNum: number) => {
    if (rakingSeats.has(seatNum)) return; // Already raking

    setRakingSeats((prev) => new Set(prev).add(seatNum));

    // Add all 3 cards for this seat
    const newCards = CARD_OFFSETS.map((_, cardIndex) => ({
      seat: seatNum,
      card: cardIndex,
      id: nextCardId.current++,
    }));

    setAnimatingCards((prev) => [...prev, ...newCards]);
  }, [rakingSeats]);

  // Rake all seats
  const rakeAllSeats = useCallback(() => {
    VISUAL_POSITIONS.forEach((seat, index) => {
      setTimeout(() => {
        rakeSeat(seat.visualPos);
      }, index * 100);
    });
  }, [rakeSeat]);

  // Reset all seats (show cards again)
  const resetSeats = useCallback(() => {
    setRakingSeats(new Set());
    setAnimatingCards([]);
  }, []);

  // Handle card animation complete
  const handleCardComplete = useCallback((cardId: number, seatNum: number) => {
    setAnimatingCards((prev) => {
      const updated = prev.filter((c) => c.id !== cardId);
      // If no more cards from this seat are animating, we could clear the seat
      // But for now we keep it hidden until manual reset
      return updated;
    });
  }, []);

  // Rake destination in canvas coords (y=-200 is above visible canvas)
  const rakeEndY = RAKE_DESTINATION.y;

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-black/50 px-6 py-4 border-b border-white/10">
        <h1 className="text-2xl font-bold">Rake Arc Animation Tuning</h1>
        <p className="text-gray-400 text-sm">
          Adjust the arc parameters to perfect the rake animation trajectory
        </p>
      </div>

      <div className="flex">
        {/* Main canvas area - extra top padding for rake destination visibility */}
        <div className="flex-1 p-4 pt-20">
          <div
            ref={containerRef}
            className="relative mx-auto bg-green-900/50 rounded-xl overflow-visible"
            style={{
              width: '100%',
              maxWidth: `${CANVAS_WIDTH}px`,
              aspectRatio: `${CANVAS_WIDTH} / ${CANVAS_HEIGHT}`,
            }}
          >
            {/* Scaled content container */}
            <div
              className="absolute inset-0 origin-top-left overflow-visible"
              style={{
                width: `${CANVAS_WIDTH}px`,
                height: `${CANVAS_HEIGHT}px`,
                transform: `scale(${scale})`,
              }}
            >
              {/* Table background */}
              <div
                className="absolute bg-green-800/60 rounded-[50%]"
                style={{
                  left: '3%',
                  right: '3%',
                  top: '10%',
                  bottom: '25%',
                }}
              />

              {/* Dealer Area marker */}
              <div
                className="absolute w-48 h-24 bg-black/30 rounded-xl border-2 border-white/30 flex items-center justify-center"
                data-dealer-area
                style={{
                  left: '50%',
                  top: '20%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <span className="text-white/60 font-bold">DEALER</span>
              </div>

              {/* Seat position markers and static cards */}
              {VISUAL_POSITIONS.map((seat) => {
                const seatPos = getSeatCanvasPosition(seat.visualPos);
                const isRaking = rakingSeats.has(seat.visualPos);

                return (
                  <g key={seat.visualPos}>
                    {/* Seat marker */}
                    <div
                      className={`absolute w-32 h-20 rounded-xl border-2 flex items-center justify-center cursor-pointer transition-all ${
                        selectedSeat === seat.visualPos
                          ? 'border-yellow-400 bg-yellow-400/20'
                          : 'border-white/30 bg-black/30'
                      }`}
                      style={{
                        left: `${seat.x}%`,
                        top: `${seat.y}%`,
                        transform: `translate(-50%, -50%) rotate(${seat.rotation}deg)`,
                      }}
                      onClick={() => setSelectedSeat(seat.visualPos)}
                    >
                      <span
                        className="text-2xl font-bold"
                        style={{ transform: `rotate(${-seat.rotation}deg)` }}
                      >
                        {seat.visualPos}
                      </span>
                    </div>

                    {/* Static cards in seat (hidden when raking) */}
                    {CARD_OFFSETS.map((_, cardIndex) => {
                      const cardPos = getCardPosition(seat.visualPos, cardIndex);
                      return (
                        <SeatCard
                          key={`static-${seat.visualPos}-${cardIndex}`}
                          x={cardPos.x}
                          y={cardPos.y}
                          rotation={cardPos.rotation}
                          hidden={isRaking}
                        />
                      );
                    })}
                  </g>
                );
              })}

              {/* SVG overlay for arc paths - extended viewBox to show rake destination above canvas */}
              <svg
                className="absolute pointer-events-none"
                style={{
                  left: 0,
                  top: '-300px', // Extend above canvas to show rake destination
                  width: `${CANVAS_WIDTH}px`,
                  height: `${CANVAS_HEIGHT + 300}px`,
                }}
                viewBox={`0 -300 ${CANVAS_WIDTH} ${CANVAS_HEIGHT + 300}`}
                preserveAspectRatio="none"
              >
                {/* Rake destination marker */}
                <circle
                  cx={RAKE_DESTINATION.x}
                  cy={rakeEndY}
                  r={30}
                  fill="red"
                  opacity={0.6}
                />
                <text
                  x={RAKE_DESTINATION.x}
                  y={rakeEndY - 50}
                  textAnchor="middle"
                  fill="white"
                  fontSize={20}
                >
                  RAKE DEST
                </text>

                {/* Arc paths from each card position */}
                {showPaths &&
                  VISUAL_POSITIONS.map((seat) => {
                    const isSelected = seat.visualPos === selectedSeat;

                    return CARD_OFFSETS.map((_, cardIndex) => {
                      const cardPos = getCardPosition(seat.visualPos, cardIndex);
                      const controlPoint = calculateControlPoint(
                        cardPos.x,
                        cardPos.y,
                        arcConfig
                      );

                      const colors = ['#ef4444', '#22c55e', '#3b82f6']; // red, green, blue
                      const color = colors[cardIndex];

                      return (
                        <g key={`path-${seat.visualPos}-${cardIndex}`}>
                          {/* Arc path */}
                          <path
                            d={quadraticBezierPath(
                              { x: cardPos.x, y: cardPos.y },
                              controlPoint,
                              { x: RAKE_DESTINATION.x, y: rakeEndY }
                            )}
                            fill="none"
                            stroke={isSelected ? color : '#ffffff'}
                            strokeWidth={isSelected ? 3 : 1}
                            strokeDasharray={isSelected ? 'none' : '4 4'}
                            opacity={isSelected ? 0.9 : 0.2}
                          />

                          {/* Control point marker (only for selected seat) */}
                          {isSelected && (
                            <circle
                              cx={controlPoint.x}
                              cy={controlPoint.y}
                              r={6}
                              fill={color}
                              opacity={0.7}
                            />
                          )}
                        </g>
                      );
                    });
                  })}
              </svg>

              {/* Animated cards */}
              {animatingCards.map((cardData) => {
                const cardPos = getCardPosition(cardData.seat, cardData.card);
                const controlPoint = calculateControlPoint(
                  cardPos.x,
                  cardPos.y,
                  arcConfig
                );

                return (
                  <AnimatedCard
                    key={cardData.id}
                    startX={cardPos.x}
                    startY={cardPos.y}
                    controlX={controlPoint.x}
                    controlY={controlPoint.y}
                    endX={RAKE_DESTINATION.x}
                    endY={rakeEndY}
                    rotation={cardPos.rotation}
                    duration={arcConfig.duration}
                    delay={cardData.card * arcConfig.staggerDelay}
                    onComplete={() => handleCardComplete(cardData.id, cardData.seat)}
                  />
                );
              })}
            </div>
          </div>
        </div>

        {/* Control Panel */}
        <div className="w-80 bg-black/70 p-4 border-l border-white/10 space-y-4 overflow-y-auto max-h-screen">
          <div className="font-bold text-lg border-b border-white/20 pb-2">
            Arc Configuration
          </div>

          {/* Seat Rake Buttons */}
          <div>
            <label className="text-sm text-gray-300 mb-2 block font-medium">
              Rake Individual Seats:
            </label>
            <div className="grid grid-cols-4 gap-2">
              {[1, 2, 3, 4, 5, 6, 7].map((seatNum) => (
                <button
                  key={seatNum}
                  onClick={() => rakeSeat(seatNum)}
                  disabled={rakingSeats.has(seatNum)}
                  className={`py-2 rounded text-sm font-bold transition-colors ${
                    rakingSeats.has(seatNum)
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                      : selectedSeat === seatNum
                      ? 'bg-yellow-500 hover:bg-yellow-400 text-black'
                      : 'bg-green-600 hover:bg-green-500 text-white'
                  }`}
                >
                  {seatNum}
                </button>
              ))}
              <button
                onClick={rakeAllSeats}
                className="py-2 bg-blue-600 hover:bg-blue-500 rounded text-sm font-bold transition-colors"
              >
                ALL
              </button>
            </div>
          </div>

          {/* Reset button */}
          <button
            onClick={resetSeats}
            className="w-full py-2 bg-orange-600 hover:bg-orange-500 rounded text-sm font-medium transition-colors"
          >
            Reset Cards
          </button>

          <div className="border-t border-white/20 pt-4" />

          {/* Control Point Pull slider */}
          <div>
            <label className="text-sm text-gray-300 mb-1 block">
              Control Point Pull: {arcConfig.controlPointPull.toFixed(2)}
            </label>
            <p className="text-xs text-gray-500 mb-2">
              0 = straight, 1 = through dealer
            </p>
            <input
              type="range"
              min="0"
              max="1.5"
              step="0.05"
              value={arcConfig.controlPointPull}
              onChange={(e) =>
                setArcConfig((prev) => ({
                  ...prev,
                  controlPointPull: parseFloat(e.target.value),
                }))
              }
              className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          {/* Control Point Y slider */}
          <div>
            <label className="text-sm text-gray-300 mb-1 block">
              Control Point Y: {arcConfig.controlPointY.toFixed(0)}%
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Vertical position of arc peak
            </p>
            <input
              type="range"
              min="-20"
              max="60"
              step="1"
              value={arcConfig.controlPointY}
              onChange={(e) =>
                setArcConfig((prev) => ({
                  ...prev,
                  controlPointY: parseFloat(e.target.value),
                }))
              }
              className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          {/* Duration slider */}
          <div>
            <label className="text-sm text-gray-300 mb-1 block">
              Duration: {arcConfig.duration}ms
            </label>
            <input
              type="range"
              min="200"
              max="1500"
              step="50"
              value={arcConfig.duration}
              onChange={(e) =>
                setArcConfig((prev) => ({
                  ...prev,
                  duration: parseInt(e.target.value),
                }))
              }
              className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          {/* Stagger delay slider */}
          <div>
            <label className="text-sm text-gray-300 mb-1 block">
              Card Stagger: {arcConfig.staggerDelay}ms
            </label>
            <input
              type="range"
              min="0"
              max="200"
              step="10"
              value={arcConfig.staggerDelay}
              onChange={(e) =>
                setArcConfig((prev) => ({
                  ...prev,
                  staggerDelay: parseInt(e.target.value),
                }))
              }
              className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          {/* Show paths toggle */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="showPaths"
              checked={showPaths}
              onChange={(e) => setShowPaths(e.target.checked)}
              className="w-4 h-4"
            />
            <label htmlFor="showPaths" className="text-sm text-gray-300">
              Show arc paths
            </label>
          </div>

          <div className="border-t border-white/20 pt-4" />

          {/* Reset to defaults */}
          <button
            onClick={() => setArcConfig(DEFAULT_ARC_CONFIG)}
            className="w-full py-2 bg-gray-600 hover:bg-gray-500 rounded text-sm font-medium transition-colors"
          >
            Reset Config to Defaults
          </button>

          {/* Export config */}
          <button
            onClick={() => {
              const configStr = JSON.stringify(arcConfig, null, 2);
              console.log('ARC_CONFIG:', configStr);
              navigator.clipboard.writeText(configStr);
            }}
            className="w-full py-2 bg-purple-600 hover:bg-purple-500 rounded text-sm font-medium transition-colors"
          >
            Copy Config to Clipboard
          </button>

          <pre className="text-xs bg-black/50 p-2 rounded overflow-auto">
            {JSON.stringify(arcConfig, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  );
}

export default Demo2Page;
